import React, { useMemo, useState, useCallback } from 'react';
import { Box, Grid, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HourlyDetailTable from './HourlyDetailTable';

function formatValue(val) {
  if (val == null || Number.isNaN(Number(val))) return '-';
  return parseFloat(Number(val).toFixed(2)).toString();
}

function MetricCard({ objective, allObjectives, type, colors, monthLabel, locale, isFirst, isLast, incidencias }) {
  const { t } = useTranslation();
  const unit = type === 'disponibilidad' ? '%' : 's';
  const chartTitle = type === 'disponibilidad' ? t('ind.tab.disponibilidad') : t('ind.tab.tiempo');

  const [hourlyModalOpen, setHourlyModalOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(0);

  const handlePointClick = useCallback((e) => {
    setSelectedMonth(e.point.index);
    setHourlyModalOpen(true);
  }, []);

  const statusColor = useMemo(() => {
    if (type !== 'disponibilidad') return colors.statusGreen;
    const val = objective.average;
    if (val == null || Number.isNaN(Number(val))) return '#848A9C';
    if (val >= 98) return '#40BB02';
    if (val >= 95) return '#FEBC09';
    return '#B21322';
  }, [type, objective.average, colors.statusGreen]);

  const availabilityValues = useMemo(
    () => {
      const source = type === 'disponibilidad' && objective.dataProgramado
        ? objective.dataProgramado
        : objective.data;
      return (source || []).filter(v => v != null);
    },
    [objective.data, objective.dataProgramado, type]
  );

  const monthLabels = useMemo(() => {
    if (!objective.months?.length) return [];
    return objective.months.map(m => {
      const [year, mon] = m.split('-');
      const d = new Date(Number(year), Number(mon) - 1);
      const label = d.toLocaleDateString(locale, { month: 'short' });
      return `${label.charAt(0).toUpperCase() + label.slice(1).replace('.', '')}\n${year}`;
    });
  }, [objective.months, locale]);

  const chartOptions = useMemo(() => ({
    chart: {
      type: 'areaspline',
      height: 220,
      backgroundColor: 'transparent',
      spacingTop: 8,
      spacingBottom: 5,
      spacingLeft: 0,
      spacingRight: 10,
    },
    title: {
      text: chartTitle,
      align: 'left',
      style: { fontSize: '11px', fontWeight: '600', color: '#666' },
      margin: 12,
    },
    xAxis: {
      categories: monthLabels,
      labels: {
        style: { fontSize: '8px', color: '#999', textAlign: 'center' },
        useHTML: false,
      },
      tickLength: 0,
      lineColor: '#e0e0e0',
      title: {
        text: 'Día',
        align: 'high',
        style: { fontSize: '10px', color: '#999', fontWeight: '400' },
        offset: 0,
        x: 10,
        y: -8,
      },
    },
    yAxis: {
      min: type === 'disponibilidad'
        ? (availabilityValues.length ? Math.floor(Math.min(...availabilityValues) - 1) : 0)
        : 0,
      max: type === 'disponibilidad' ? 100 : undefined,
      tickInterval: 1,
      title: { text: null },
      gridLineDashStyle: 'Dash',
      gridLineColor: '#e8e8e8',
      labels: { style: { fontSize: '10px', color: '#999' } },
    },
    series: [
      {
        name: chartTitle,
        data: (
          type === 'disponibilidad' && objective.dataProgramado
            ? objective.dataProgramado
            : (objective.data || [])
        ).map((v, i) => ({
          y: v,
          incidentCount: objective.incidentCounts ? objective.incidentCounts[i] : 0,
          realValue: type === 'disponibilidad' && objective.data ? objective.data[i] : null,
          programadoValue: type === 'disponibilidad' && objective.dataProgramado ? objective.dataProgramado[i] : null,
        })),
        color: colors.chartLine,
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, 'rgba(82, 44, 157, 0.12)'],
            [1, 'rgba(82, 44, 157, 0)'],
          ],
        },
        lineWidth: 2,
        marker: {
          enabled: true,
          radius: 5,
          fillColor: '#FFFFFF',
          lineWidth: 2,
          lineColor: colors.chartLine,
          symbol: 'circle',
        },
      },
    ],
    legend: { enabled: false },
    credits: { enabled: false },
    tooltip: {
      useHTML: true,
      formatter: function () {
        const p = this.point;
        const inc = p.incidentCount || 0;
        let html = '<b>' + this.key + '</b>';
        if (type === 'disponibilidad' && p.programadoValue != null && p.realValue != null) {
          html += '<br/>' + t('ind.hourly.programado') + ': '
            + Highcharts.numberFormat(p.programadoValue, 2) + unit;
          html += '<br/>' + t('ind.hourly.real') + ': '
            + Highcharts.numberFormat(p.realValue, 2) + unit;
        } else {
          html += '<br/>' + Highcharts.numberFormat(this.y, 2) + unit;
        }
        if (inc > 0) {
          html += '<br/><span style="color:#0A1938">Incidencias: ' + inc + '</span>';
        }
        return html;
      },
    },
    plotOptions: {
      areaspline: {
        marker: { enabled: true },
        cursor: 'pointer',
        point: {
          events: {
            click: handlePointClick,
          },
        },
      },
    },
  }), [availabilityValues, objective.data, objective.dataProgramado, objective.incidentCounts, type, colors, monthLabels, chartTitle, unit, handlePointClick, t]);

  return (
    <Box pb={2} pt={isFirst ? 0 : 2} style={!isLast ? { borderBottom: '1px solid #f0f0f0' } : {}}>
      <Box display="flex" alignItems="center" mb={2}>
        <Box style={{
          width: 36, height: 36, borderRadius: 18,
          backgroundColor: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Box style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: statusColor }} />
        </Box>
        <Typography variant="h6" style={{ fontWeight: 700, fontSize: '1.1rem', color: colors.textPrimary }}>
          {objective.name}
        </Typography>
      </Box>
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} sm={3}>
          <Box display="flex" flexDirection="column" justifyContent="center" height="100%" style={{ gap: 12 }}>
            <Box style={{
              backgroundColor: colors.statBoxBg, borderRadius: 16,
              padding: '14px 16px',
            }}>
              <Typography variant="caption" style={{ color: colors.textSecondary, fontSize: '0.75rem', display: 'block', marginBottom: 2 }}>
                {monthLabel}
              </Typography>
              <Typography variant="h4" style={{ fontWeight: 700, fontSize: '1.9rem', lineHeight: 1.2, color: colors.textPrimary }}>
                {formatValue(
                  type === 'disponibilidad' && objective.currentMonthProgramado != null
                    ? objective.currentMonthProgramado
                    : objective.currentMonth
                )}{unit}
              </Typography>
            </Box>
            <Box style={{
              backgroundColor: colors.statBoxBgAlt, borderRadius: 16,
              padding: '14px 16px',
            }}>
              <Typography variant="caption" style={{ color: colors.textSecondary, fontSize: '0.75rem', display: 'block', marginBottom: 2 }}>
                {t('ind.average')}
              </Typography>
              <Typography variant="h4" style={{ fontWeight: 700, fontSize: '1.9rem', lineHeight: 1.2, color: colors.textPrimary }}>
                {formatValue(
                  type === 'disponibilidad' && objective.averageProgramado != null
                    ? objective.averageProgramado
                    : objective.average
                )}{unit}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={9}>
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </Grid>
      </Grid>
      <HourlyDetailTable
        open={hourlyModalOpen}
        onClose={() => setHourlyModalOpen(false)}
        objective={objective}
        monthIndex={selectedMonth}
        type={type}
        colors={colors}
        locale={locale}
        incidencias={incidencias}
      />
    </Box>
  );
}

export default MetricCard;
